import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { ArrowDown, ArrowUp, Archive, ChevronDown, ChevronLeft, ChevronRight, ChevronsUpDown, Heart, Thermometer, Wind, Download, Mail, Share2, Loader2, Plus, Droplets, Scale, CheckCircle, RotateCcw, Trash2, CircleArrowDown, MoreVertical, PartyPopper, Angry, Frown, Meh, Smile, Laugh, Info } from 'lucide-react';
import { cn } from '../lib/utils';
import { format, parseISO, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, isWithinInterval, addDays, isSameMonth, isSameDay, isToday, addMonths, subMonths } from 'date-fns';
import Trends from './Trends';
import { formatWeight } from '../functions/formatWeight';
import { formatFluid } from '../functions/formatFluid';
import { useDeleteConfirm } from '../hooks/useDeleteConfirm';
import { useToast } from '../hooks/useToast';
import { Modal } from '../components/Modal';
import { Dropdown } from '../components/Dropdown';
import { formatTemp } from '../functions/formatTemp';
import { formatDateByPreference } from '../functions/formatDateByPreference';
import { getDateLocale } from '../functions/getDateLocale';
import { useSettings } from '../functions/useSettings';
import { useLanguage } from '../functions/useLanguage';
import { useBeanActions } from '../hooks/useBeanActions';
import { useTodaySteps } from '../hooks/useTodaySteps';
import { TodayStep } from '../components/TodayStep';
import { TodayToolbar } from '../components/TodayToolbar';
import type { MucousMembraneColor } from '../components/TodayToolbar';
import html2canvas from 'html2canvas-pro';
import KodaCareLogo from '../components/Custom Icons/KodaCareLogo';
import MucousMembraneIcon from '../components/Custom Icons/MucousMembraneIcon';
import { useRecordsTab } from '../context/RecordsTabContext';
import { usePet } from '../context/PetContext';
import { loadDiaryEntries, addDiaryEntry, type DiaryEntry } from '../lib/diary';
import { ThemedDateField } from '../components/ThemedDateField';
import { TabSelector } from '../components/TabSelector';
import { OverviewCard } from '../components/OverviewCard';
import { Squircle } from '@squircle-js/react';

const INITIAL_FLUID_CHIP_COUNTS: Record<number, number> = {
  500: 0,
  1000: 0,
  1500: 0,
  3000: 0,
};

const INITIAL_FOOD_CHIP_COUNTS: Record<number, number> = {
  25: 0,
  100: 0,
  250: 0,
  500: 0,
};

type FluidEntryMeta = { source: 'chip' | 'manual'; amount: number };

export default function Vitals() {
  const { user, firebaseUser } = useAuth();
  const { activePet, activePetId } = usePet();
  // uid is available immediately from the cached Firebase Auth token.
  // user (UserProfile) may be null on slow mobile connections if the Firestore
  // profile fetch times out — never use user!.id or user?.id for data ops.
  const uid = firebaseUser?.uid ?? '';
  const { t } = useLanguage();
  const { logs, updateLog, deleteLog, addLog, deleteLogs } = useData();
  const { onTodayCardComplete, rollbackStreak } = useBeanActions();
  const { tab, setTab } = useRecordsTab();
  const { isMetric, dateFormat, use24h } = useSettings();
  const { requestDelete, modal: deleteModal } = useDeleteConfirm();
  const { showToast, ToastMount } = useToast();

  // Reset scroll to top on tab change for desktop/tablet (mobile handled by BottomBar)
  useEffect(() => {
    if (window.innerWidth >= 640) {
      const main = document.getElementById('main-scroll');
      if (main) main.scrollTop = 0;
    }
  }, [tab]);

  // ── Cards (dashboard tab) state ──────────────────────────────────────────
  const toMetricTemp = (value: number) => isMetric ? value : (value - 32) * 5 / 9;

  // Today's Card step completion flags
  const [hrDone,   setHrDone]   = useState(false);
  const [rrDone,   setRrDone]   = useState(false);
  const [tempDone, setTempDone] = useState(false);
  const [weightDone, setWeightDone] = useState(false);

  // Step input values
  const [hrCount,      setHrCount]      = useState('');
  const [rrCount,      setRrCount]      = useState('');
  const [tempInput,    setTempInput]    = useState('');
  const [weightStepInput, setWeightStepInput] = useState('');

  // Inline 15s timers for HR and RR steps
  const [hrTimerSec, setHrTimerSec] = useState<number | null>(null);
  const [rrTimerSec, setRrTimerSec] = useState<number | null>(null);
  const [hrPreCount, setHrPreCount] = useState<number | null>(null); // 1→2→3→4(GO!)
  const [rrPreCount, setRrPreCount] = useState<number | null>(null);
  const hrTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const rrTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopHrTimer = () => {
    if (hrTimerRef.current) { clearInterval(hrTimerRef.current); hrTimerRef.current = null; }
    setHrTimerSec(null);
    setHrPreCount(null);
  };

  const stopRrTimer = () => {
    if (rrTimerRef.current) { clearInterval(rrTimerRef.current); rrTimerRef.current = null; }
    setRrTimerSec(null);
    setRrPreCount(null);
  };

  const startHrTimer = () => {
    stopHrTimer();
    setHrPreCount(1);
    let count = 1;
    hrTimerRef.current = setInterval(() => {
      count += 1;
      if (count <= 4) {
        setHrPreCount(count);
        if (count === 4) {
          clearInterval(hrTimerRef.current!);
          setTimeout(() => {
            setHrPreCount(null);
            setHrTimerSec(15);
            hrTimerRef.current = setInterval(() => {
              setHrTimerSec(prev => {
                if (prev === null || prev <= 1) {
                  clearInterval(hrTimerRef.current!); hrTimerRef.current = null;
                  setTimeout(() => setHrTimerSec(null), 3000);
                  return 0;
                }
                return prev - 1;
              });
            }, 1000);
          }, 600);
        }
      }
    }, 1000);
  };

  const startRrTimer = () => {
    stopRrTimer();
    setRrPreCount(1);
    let count = 1;
    rrTimerRef.current = setInterval(() => {
      count += 1;
      if (count <= 4) {
        setRrPreCount(count);
        if (count === 4) {
          clearInterval(rrTimerRef.current!);
          setTimeout(() => {
            setRrPreCount(null);
            setRrTimerSec(15);
            rrTimerRef.current = setInterval(() => {
              setRrTimerSec(prev => {
                if (prev === null || prev <= 1) {
                  clearInterval(rrTimerRef.current!); rrTimerRef.current = null;
                  setTimeout(() => setRrTimerSec(null), 3000);
                  return 0;
                }
                return prev - 1;
              });
            }, 1000);
          }, 600);
        }
      }
    }, 1000);
  };

  // Today's logs for each step (for undo + display)
  const [hrLog,   setHrLog]   = useState<any>(null);
  const [rrLog,   setRrLog]   = useState<any>(null);
  const [tempLog, setTempLog] = useState<any>(null);

  // ── Diary state ──────────────────────────────────────────────────────────
  const [diaryEntries, setDiaryEntries] = useState<DiaryEntry[]>(() => loadDiaryEntries(activePetId));
  const [currentDayKey, setCurrentDayKey] = useState(() => format(new Date(), 'yyyy-MM-dd'));

  useEffect(() => {
    let timeoutId: number | undefined;

    const scheduleNextMidnightTick = () => {
      const now = new Date();
      const nextMidnight = new Date(now);
      nextMidnight.setHours(24, 0, 0, 0);
      const delay = Math.max(1000, nextMidnight.getTime() - now.getTime() + 50);

      timeoutId = window.setTimeout(() => {
        setCurrentDayKey(format(new Date(), 'yyyy-MM-dd'));
        scheduleNextMidnightTick();
      }, delay);
    };

    setCurrentDayKey(format(new Date(), 'yyyy-MM-dd'));
    scheduleNextMidnightTick();

    return () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  const todayDiaryEntries = useMemo(
    () => diaryEntries.filter(entry => format(new Date(entry.createdAt), 'yyyy-MM-dd') === currentDayKey),
    [diaryEntries, currentDayKey],
  );

  useEffect(() => {
    setDiaryEntries(loadDiaryEntries(activePetId));
  }, [activePetId]);

  const handleDiarySave = (html: string, mood: number | null) => {
    const updated = addDiaryEntry(html, mood, activePetId);
    setDiaryEntries(updated);
  };

  // Wellness Check panel state
  const [totalFluidIn,       setTotalFluidIn]       = useState(0);
  const [totalFoodIn,        setTotalFoodIn]        = useState(0);
  const [todayWeight,        setTodayWeight]        = useState<number | null>(null);
  const [todayMucousMembrane, setTodayMucousMembrane] = useState<MucousMembraneColor | null>(null);
  const [mucousMembraneLog,  setMucousMembraneLog]  = useState<any>(null);
  const [fluidChipCounts,    setFluidChipCounts]    = useState<Record<number, number>>(INITIAL_FLUID_CHIP_COUNTS);
  const [foodChipCounts,     setFoodChipCounts]     = useState<Record<number, number>>(INITIAL_FOOD_CHIP_COUNTS);
  const [fluidEntryStack,    setFluidEntryStack]    = useState<FluidEntryMeta[]>([]);
  const [foodEntryStack,     setFoodEntryStack]     = useState<FluidEntryMeta[]>([]);

  // Observations
  const [observationsDone,   setObservationsDone]   = useState(false);
  const [observationsInput,  setObservationsInput]  = useState('');
  const [observationsMood,   setObservationsMood]   = useState<number | null>(null);
  const [observationsLog,    setObservationsLog]    = useState<any>(null);

  useEffect(() => {
    const dateStr = format(new Date(), 'yyyy-MM-dd');
    const selectedLogs = logs.filter(l => l.timestamp.startsWith(dateStr));

    // Fluid
    const fluidIn = selectedLogs.reduce((sum, l) => sum + (l.fluid_in_ml || 0), 0);
    setTotalFluidIn(fluidIn);
    const fluidLogs = selectedLogs.filter(l => l.fluid_in_ml);
    setFluidChipCounts({
      500:  fluidLogs.filter(l => l.fluid_in_ml === 500).length,
      1000: fluidLogs.filter(l => l.fluid_in_ml === 1000).length,
      1500: fluidLogs.filter(l => l.fluid_in_ml === 1500).length,
      3000: fluidLogs.filter(l => l.fluid_in_ml === 3000).length,
    });

    // Food
    const foodIn = selectedLogs.reduce((sum, l) => sum + (l.food_in_mg || 0), 0);
    setTotalFoodIn(foodIn);
    const foodLogs = selectedLogs.filter(l => l.food_in_mg);
    setFoodChipCounts({
      25:  foodLogs.filter(l => l.food_in_mg === 25).length,
      100: foodLogs.filter(l => l.food_in_mg === 100).length,
      250: foodLogs.filter(l => l.food_in_mg === 250).length,
      500: foodLogs.filter(l => l.food_in_mg === 500).length,
    });

    // Weight
    const weightLog = selectedLogs.find(l => l.weight_kg != null);
    setTodayWeight(weightLog?.weight_kg ?? null);
    setWeightDone(weightLog != null);

    // Heart Rate
    const hrLogFound = selectedLogs.find(l => l.heart_rate_bpm != null);
    setHrLog(hrLogFound ?? null);
    setHrDone(!!hrLogFound);

    // Respiratory Rate
    const rrLogFound = selectedLogs.find(l => l.resp_rate_bpm != null);
    setRrLog(rrLogFound ?? null);
    setRrDone(!!rrLogFound);

    // Temperature
    const tempLogFound = selectedLogs.find(l => l.temp_c != null);
    setTempLog(tempLogFound ?? null);
    setTempDone(!!tempLogFound);

    // Mucous membrane
    const mmLog = selectedLogs.find(l => l.mucous_membrane != null);
    setMucousMembraneLog(mmLog ?? null);
    setTodayMucousMembrane((mmLog?.mucous_membrane as MucousMembraneColor) ?? null);

    // Observations
    const obsLog = selectedLogs.find(l => l.observations_text !== undefined || l.mood !== undefined);
    if (obsLog) { setObservationsDone(true); setObservationsLog(obsLog); }
    else { setObservationsDone(false); setObservationsLog(null); }
  }, [logs, uid]);

  // ── Today's Card step state ──────────────────────────────────────────────
  const { steps: todaySteps, doneCount, allDone } = useTodaySteps({
    hrDone,
    rrDone,
    weightDone,
  });

  // Fire streak + XP the first time all 5 steps are completed today
  const allDoneRef = React.useRef(false);
  useEffect(() => {
    if (allDone && !allDoneRef.current) {
      allDoneRef.current = true;
      onTodayCardComplete();
    }
    if (!allDone) allDoneRef.current = false;
  }, [allDone, onTodayCardComplete]);

  // ── Step handlers ────────────────────────────────────────────────────────

  const handleSaveHeartRate = () => {
    const count = Number(hrCount);
    if (!count || count <= 0) return;
    const bpm = count * 4;
    const today = format(new Date(), 'yyyy-MM-dd');
    const existing = logs.find(l => l.timestamp.startsWith(today) && l.heart_rate_bpm != null);
    if (existing) updateLog(existing.id, { heart_rate_count: count, heart_rate_bpm: bpm });
    else addLog({ user_id: uid, timestamp: new Date().toISOString(), heart_rate_count: count, heart_rate_bpm: bpm });
    setHrCount('');
  };

  const handleUndoHeartRate = () => {
    if (!hrLog) return;
    const today = format(new Date(), 'yyyy-MM-dd');
    const todayLogs = logs.filter(l => l.timestamp.startsWith(today));
    const hasRR     = todayLogs.some(l => l.resp_rate_bpm != null);
    const hasWeight = todayLogs.some(l => l.weight_kg != null);
    if (hasRR && hasWeight) rollbackStreak();
    deleteLog(hrLog.id);
  };

  const handleSaveRespRate = () => {
    const count = Number(rrCount);
    if (!count || count <= 0) return;
    const bpm = count * 4;
    const today = format(new Date(), 'yyyy-MM-dd');
    const existing = logs.find(l => l.timestamp.startsWith(today) && l.resp_rate_bpm != null);
    if (existing) updateLog(existing.id, { resp_rate_count: count, resp_rate_bpm: bpm });
    else addLog({ user_id: uid, timestamp: new Date().toISOString(), resp_rate_count: count, resp_rate_bpm: bpm });
    setRrCount('');
  };

  const handleUndoRespRate = () => {
    if (!rrLog) return;
    const today = format(new Date(), 'yyyy-MM-dd');
    const todayLogs = logs.filter(l => l.timestamp.startsWith(today));
    const hasHR     = todayLogs.some(l => l.heart_rate_bpm != null);
    const hasWeight = todayLogs.some(l => l.weight_kg != null);
    if (hasHR && hasWeight) rollbackStreak();
    deleteLog(rrLog.id);
  };

  const handleSaveTemp = (tempC?: number) => {
    let valueC: number;
    if (tempC != null) {
      valueC = tempC;
    } else {
      const raw = Number(tempInput);
      if (!raw || raw <= 0) return;
      valueC = toMetricTemp(raw);
      setTempInput('');
    }
    if (!valueC || valueC <= 0) return;
    const today = format(new Date(), 'yyyy-MM-dd');
    const existing = logs.find(l => l.timestamp.startsWith(today) && l.temp_c != null);
    if (existing) updateLog(existing.id, { temp_c: valueC });
    else addLog({ user_id: uid, timestamp: new Date().toISOString(), temp_c: valueC });
  };

  const handleUndoTemp = () => {
    if (!tempLog) return;
    const today = format(new Date(), 'yyyy-MM-dd');
    const todayLogs = logs.filter(l => l.timestamp.startsWith(today));
    const hasHR = todayLogs.some(l => l.heart_rate_bpm != null);
    const hasRR = todayLogs.some(l => l.resp_rate_bpm != null);
    if (hasHR && hasRR) rollbackStreak();
    deleteLog(tempLog.id);
  };

  // ── Wellness Check handlers ──────────────────────────────────────────────

  const handleSaveWeight = (kg: number) => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const existing = logs.find(l => l.timestamp.startsWith(today) && l.weight_kg != null);
    if (existing) updateLog(existing.id, { weight_kg: kg });
    else addLog({ user_id: uid, timestamp: new Date().toISOString(), weight_kg: kg });
  };

  const handleSaveWeightStep = () => {
    const raw = Number(weightStepInput);
    if (!raw || raw <= 0) return;
    const kg = isMetric ? raw : raw / 2.20462;
    handleSaveWeight(kg);
    setWeightStepInput('');
  };

  const handleUndoWeight = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const wLog = logs.find(l => l.timestamp.startsWith(today) && l.weight_kg != null);
    if (!wLog) return;
    const todayLogs = logs.filter(l => l.timestamp.startsWith(today));
    const hasHR = todayLogs.some(l => l.heart_rate_bpm != null);
    const hasRR = todayLogs.some(l => l.resp_rate_bpm != null);
    if (hasHR && hasRR) rollbackStreak();
    deleteLog(wLog.id);
  };

  const handleSaveMucousMembrane = (color: MucousMembraneColor) => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const existing = logs.find(l => l.timestamp.startsWith(today) && l.mucous_membrane != null);
    if (existing) updateLog(existing.id, { mucous_membrane: color });
    else addLog({ user_id: uid, timestamp: new Date().toISOString(), mucous_membrane: color });
  };

  // ── Fluid handlers ───────────────────────────────────────────────────────

  const handleAddFluid = (amount: number, source: 'chip' | 'manual' = 'manual') => {
    if (totalFluidIn + amount > 9999) { showToast(t('records.dailyLimitReached'), 'warning'); return; }
    addLog({ user_id: uid, timestamp: new Date().toISOString(), fluid_in_ml: amount });
    setFluidEntryStack(prev => [...prev, { source, amount }]);
  };

  const handleUndoFluid = () => {
    const dateStr = format(new Date(), 'yyyy-MM-dd');
    const latestLog = [...logs]
      .filter(l => l.timestamp.startsWith(dateStr) && l.fluid_in_ml)
      .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
      .at(-1);
    if (latestLog) {
      deleteLog(latestLog.id);
      setFluidEntryStack(prev => prev.length ? prev.slice(0, -1) : prev);
    }
  };

  const handleChipFluidClick = (amount: number) => handleAddFluid(amount, 'chip');

  const handleAddFood = (amount: number, source: 'chip' | 'manual' = 'manual') => {
    if (totalFoodIn + amount > 99999) { showToast(t('records.dailyLimitReached'), 'warning'); return; }
    addLog({ user_id: uid, timestamp: new Date().toISOString(), food_in_mg: amount });
    setFoodEntryStack(prev => [...prev, { source, amount }]);
  };

  const handleUndoFood = () => {
    const dateStr = format(new Date(), 'yyyy-MM-dd');
    const latestLog = [...logs]
      .filter(l => l.timestamp.startsWith(dateStr) && l.food_in_mg)
      .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
      .at(-1);
    if (latestLog) {
      deleteLog(latestLog.id);
      setFoodEntryStack(prev => prev.length ? prev.slice(0, -1) : prev);
    }
  };

  const handleSaveObservations = () => {
    if (observationsInput.trim() === '' && observationsMood === null) return;
    const today = format(new Date(), 'yyyy-MM-dd');
    const existingObsLog = logs.find(l => l.timestamp.startsWith(today) && (l.observations_text !== undefined || l.mood !== undefined));
    const data: any = {
      ...(observationsInput.trim() ? { observations_text: observationsInput.trim() } : {}),
      ...(observationsMood !== null ? { mood: observationsMood } : {}),
    };
    if (existingObsLog) updateLog(existingObsLog.id, data);
    else addLog({ user_id: uid, timestamp: new Date().toISOString(), ...data });
    // Do NOT clear — keep state visible so further edits update the record
  };

  const handleUndoObservations = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const obsLog = logs.find(l => l.timestamp.startsWith(today) && (l.observations_text !== undefined || l.mood !== undefined));
    if (obsLog) deleteLog(obsLog.id);
  };

  const handleResetFluidIntake = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const fluidLogs = logs.filter(l => l.timestamp.startsWith(today) && l.fluid_in_ml);
    if (fluidLogs.length > 0) deleteLogs(fluidLogs.map(l => l.id));
    setFluidChipCounts(INITIAL_FLUID_CHIP_COUNTS);
    setFluidEntryStack([]);
  };
  // ── end Cards state ──────────────────────────────────────────────────────

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<any>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLog, setNewLog] = useState<any>({});
  const [expandedArchiveId, setExpandedArchiveId] = useState<string | null>(null);
  const [sortByPeriod, setSortByPeriod] = useState<'week' | 'month' | 'year'>('week');
  const [worksheetPage, setWorksheetPage] = useState(1);
  type WorksheetSortCol = 'date' | 'hr' | 'rr' | 'temp' | 'mm' | 'weight' | 'fluid' | 'food';
  const [worksheetSortCol, setWorksheetSortCol] = useState<WorksheetSortCol>('date');
  const [worksheetSortDir, setWorksheetSortDir] = useState<'asc' | 'desc'>('desc');
  const [allDiaryExpanded, setAllDiaryExpanded] = useState(false);
  const [worksheetEditDate, setWorksheetEditDate] = useState<string | null>(null);
  const [worksheetEditValues, setWorksheetEditValues] = useState<any>({});

  const toggleWorksheetSort = (col: WorksheetSortCol) => {
    if (worksheetSortCol === col) {
      setWorksheetSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setWorksheetSortCol(col);
      setWorksheetSortDir(col === 'date' ? 'desc' : 'asc');
    }
    setWorksheetPage(1);
  };

  // Persist active tab
  useEffect(() => {
    localStorage.setItem('koda_home_tab', tab);
  }, [tab]);

  const userLogs = useMemo(() => (
    [...logs]
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
  ), [logs]);

  const formatValue = (value: number | undefined, unit: 'weight' | 'fluid' | 'temp' | 'other'): string => {
    if (value === undefined) return '--';
    if (unit === 'weight') return isMetric ? `${value} kg` : `${(value * 2.20462).toFixed(1)} lbs`;
    if (unit === 'fluid') return isMetric ? `${value} ml` : `${(value * 0.033814).toFixed(0)} fl oz`;
    if (unit === 'temp') return isMetric ? `${value}°C` : `${(value * 9/5 + 32).toFixed(1)}°F`;
    return String(value);
  };

  const getOrdinalDay = (day: number) => {
    if (day > 3 && day < 21) return `${day}th`;
    switch (day % 10) {
      case 1: return `${day}st`;
      case 2: return `${day}nd`;
      case 3: return `${day}rd`;
      default: return `${day}th`;
    }
  };

  const handleEdit = (log: any) => {
    setEditingId(log.id);
    setEditValues(log);
  };

  const handleSaveEdit = () => {
    if (editingId) {
      updateLog(editingId, editValues);
      setEditingId(null);
    }
  };

  const handleDelete = (id: string) => {
    requestDelete(
      t('records.deleteLogEntry'),
      t('records.deleteEntryConfirm'),
      () => deleteLog(id),
    );
  };

  const handleAddLog = () => {
    const date = newLog.date || format(new Date(), 'yyyy-MM-dd');
    const hasVitals = newLog.heart_rate_count || newLog.resp_rate_count || newLog.temp_c;
    const hasWellness = newLog.weight_kg || newLog.fluid_in_ml || newLog.mucous_membrane;

    if (!hasVitals && !hasWellness) {
      showToast(t('records.fillAtLeastOneField'), 'warning');
      return;
    }

    const existingLogsForDate = logs.filter(l => l.timestamp.startsWith(date));
    if (existingLogsForDate.length > 0) {
      showToast(t('records.dateAlreadyHasEntry'), 'warning');
      return;
    }

    const ts = (h: number) => `${date}T${String(h).padStart(2, '0')}:00`;

    if (newLog.heart_rate_count) {
      const count = Number(newLog.heart_rate_count);
      addLog({ user_id: uid, timestamp: ts(9), heart_rate_count: count, heart_rate_bpm: count * 4 });
    }
    if (newLog.resp_rate_count) {
      const count = Number(newLog.resp_rate_count);
      addLog({ user_id: uid, timestamp: ts(9), resp_rate_count: count, resp_rate_bpm: count * 4 });
    }
    if (newLog.temp_c) {
      const tempC = isMetric ? Number(newLog.temp_c) : (Number(newLog.temp_c) - 32) * 5 / 9;
      addLog({ user_id: uid, timestamp: ts(9), temp_c: tempC });
    }
    if (newLog.weight_kg) {
      const kg = isMetric ? Number(newLog.weight_kg) : Number(newLog.weight_kg) / 2.20462;
      addLog({ user_id: uid, timestamp: ts(9), weight_kg: kg });
    }
    if (newLog.fluid_in_ml) {
      addLog({ user_id: uid, timestamp: ts(9), fluid_in_ml: Number(newLog.fluid_in_ml) });
    }
    if (newLog.mucous_membrane) {
      addLog({ user_id: uid, timestamp: ts(9), mucous_membrane: newLog.mucous_membrane });
    }

    setNewLog({});
    setShowAddForm(false);
  };

  // Open edit modal for a worksheet row - pre-populate with existing values
  const handleWorksheetEdit = (dateStr: string) => {
    const dateLogs = logs.filter(l => l.timestamp.startsWith(dateStr));
    const hrL  = dateLogs.find(l => l.heart_rate_bpm != null);
    const rrL  = dateLogs.find(l => l.resp_rate_bpm != null);
    const tL   = dateLogs.find(l => l.temp_c != null);
    const mmL  = dateLogs.find(l => l.mucous_membrane != null);
    const wL   = dateLogs.find(l => l.weight_kg != null);
    const totalFluidInDay = dateLogs.reduce((sum, l) => sum + (l.fluid_in_ml || 0), 0);
    const totalFoodInDay = dateLogs.reduce((sum, l) => sum + (l.food_in_mg || 0), 0);
    const obsLog = dateLogs.find(l => l.observations_text !== undefined || l.mood !== undefined);

    setWorksheetEditValues({
      heart_rate_count: hrL?.heart_rate_count || '',
      resp_rate_count: rrL?.resp_rate_count || '',
      temp_c: tL?.temp_c ? (isMetric ? tL.temp_c : (tL.temp_c * 9/5 + 32).toFixed(1)) : '',
      mucous_membrane: mmL?.mucous_membrane || '',
      weight_kg: wL?.weight_kg ? (isMetric ? wL.weight_kg : (wL.weight_kg * 2.20462).toFixed(1)) : '',
      fluid_in_ml: totalFluidInDay || '',
      food_in_g: totalFoodInDay ? (isMetric ? (totalFoodInDay / 1000).toFixed(1) : (totalFoodInDay / 28349.5).toFixed(2)) : '',
      observations_text: obsLog?.observations_text || '',
      mood: obsLog?.mood !== undefined ? obsLog.mood : null,
    });
    setWorksheetEditDate(dateStr);
  };

  // Save worksheet edit
  const handleWorksheetEditSave = () => {
    if (!worksheetEditDate) return;
    const date = worksheetEditDate;
    const dateLogs = logs.filter(l => l.timestamp.startsWith(date));
    const ts = (h: number) => `${date}T${String(h).padStart(2, '0')}:00`;

    const upsert = (existing: any, data: any) => {
      if (existing) updateLog(existing.id, data);
      else addLog({ user_id: uid, timestamp: ts(9), ...data });
    };

    if (worksheetEditValues.heart_rate_count) {
      const count = Number(worksheetEditValues.heart_rate_count);
      upsert(dateLogs.find(l => l.heart_rate_bpm != null), { heart_rate_count: count, heart_rate_bpm: count * 4 });
    }
    if (worksheetEditValues.resp_rate_count) {
      const count = Number(worksheetEditValues.resp_rate_count);
      upsert(dateLogs.find(l => l.resp_rate_bpm != null), { resp_rate_count: count, resp_rate_bpm: count * 4 });
    }
    if (worksheetEditValues.temp_c) {
      const tempC = isMetric ? Number(worksheetEditValues.temp_c) : (Number(worksheetEditValues.temp_c) - 32) * 5 / 9;
      upsert(dateLogs.find(l => l.temp_c != null), { temp_c: tempC });
    }
    if (worksheetEditValues.mucous_membrane) {
      upsert(dateLogs.find(l => l.mucous_membrane != null), { mucous_membrane: worksheetEditValues.mucous_membrane });
    }
    if (worksheetEditValues.weight_kg) {
      const kg = isMetric ? Number(worksheetEditValues.weight_kg) : Number(worksheetEditValues.weight_kg) / 2.20462;
      upsert(dateLogs.find(l => l.weight_kg != null), { weight_kg: kg });
    }
    if (worksheetEditValues.fluid_in_ml) {
      // Replace all fluid logs with a single entry
      const fluidLogs = dateLogs.filter(l => l.fluid_in_ml);
      if (fluidLogs.length > 0) {
        updateLog(fluidLogs[0].id, { fluid_in_ml: Number(worksheetEditValues.fluid_in_ml) });
        if (fluidLogs.length > 1) deleteLogs(fluidLogs.slice(1).map(l => l.id));
      } else {
        addLog({ user_id: uid, timestamp: ts(9), fluid_in_ml: Number(worksheetEditValues.fluid_in_ml) });
      }
    }
    if (worksheetEditValues.food_in_g) {
      const mg = Math.round(isMetric ? Number(worksheetEditValues.food_in_g) * 1000 : Number(worksheetEditValues.food_in_g) * 28349.5);
      // Replace all food logs with a single entry
      const foodLogs = dateLogs.filter(l => l.food_in_mg);
      if (foodLogs.length > 0) {
        updateLog(foodLogs[0].id, { food_in_mg: mg });
        if (foodLogs.length > 1) deleteLogs(foodLogs.slice(1).map(l => l.id));
      } else {
        addLog({ user_id: uid, timestamp: ts(9), food_in_mg: mg });
      }
    }
    if (worksheetEditValues.observations_text || worksheetEditValues.mood !== null) {
      const obsData: any = {
        ...(worksheetEditValues.observations_text ? { observations_text: worksheetEditValues.observations_text } : {}),
        ...(worksheetEditValues.mood !== null && worksheetEditValues.mood !== undefined ? { mood: worksheetEditValues.mood } : {}),
      };
      upsert(dateLogs.find(l => l.observations_text !== undefined || l.mood !== undefined), obsData);
    }

    setWorksheetEditDate(null);
    setWorksheetEditValues({});
  };

  // Delete all logs for a worksheet date
  const handleWorksheetDelete = (dateStr: string) => {
    requestDelete(
      t('records.deleteEntry'),
      t('records.deleteEntryConfirm'),
      () => {
        const dateLogs = logs.filter(l => l.timestamp.startsWith(dateStr));
        deleteLogs(dateLogs.map(l => l.id));
      }
    );
  };

  // Group logs by date
  const logsByDate = useMemo(() => {
    return userLogs.reduce((acc: any, log) => {
      const dateStr = log.timestamp.split('T')[0];
      if (!acc[dateStr]) {
        acc[dateStr] = [];
      }
      acc[dateStr].push(log);
      return acc;
    }, {});
  }, [userLogs]);

  // All dates sorted by chosen column
  const getAllDates = () => {
    const keys = Object.keys(logsByDate).sort(); // base: date asc
    if (worksheetSortCol === 'date') {
      return worksheetSortDir === 'desc' ? keys.reverse() : keys;
    }
    const getValue = (dateStr: string): number => {
      const dl = logsByDate[dateStr];
      switch (worksheetSortCol) {
        case 'hr':     return dl.find((l: any) => l.heart_rate_bpm != null)?.heart_rate_bpm ?? -1;
        case 'rr':     return dl.find((l: any) => l.resp_rate_bpm != null)?.resp_rate_bpm ?? -1;
        case 'temp':   return dl.find((l: any) => l.temp_c != null)?.temp_c ?? -1;
        case 'mm':     return dl.find((l: any) => l.mucous_membrane != null) ? 1 : -1;
        case 'weight': return dl.find((l: any) => l.weight_kg != null)?.weight_kg ?? -1;
        case 'fluid':  return dl.reduce((s: number, l: any) => s + (l.fluid_in_ml || 0), 0);
        case 'food':   return dl.reduce((s: number, l: any) => s + (l.food_in_mg || 0), 0);
        default:       return 0;
      }
    };
    return keys.sort((a, b) => {
      const diff = getValue(a) - getValue(b);
      return worksheetSortDir === 'asc' ? diff : -diff;
    });
  };

  const WORKSHEET_PAGE_SIZE = sortByPeriod === 'week' ? 7 : 30;
  const filteredDates = useMemo(() => getAllDates(), [logsByDate, worksheetSortCol, worksheetSortDir]);
  const worksheetTotalPages = Math.max(1, Math.ceil(filteredDates.length / WORKSHEET_PAGE_SIZE));
  const worksheetPagedDates = filteredDates.slice(
    (worksheetPage - 1) * WORKSHEET_PAGE_SIZE,
    worksheetPage * WORKSHEET_PAGE_SIZE
  );

  const buildCSV = (period: 'week' | 'month' | 'year'): { csv: string; fileName: string; periodLabel: string } => {
    const petLabel = activePet?.name || user?.full_name || 'Pet';
    const today = new Date();
    const ranges = {
      week:  { start: startOfWeek(today),  end: endOfWeek(today) },
      month: { start: startOfMonth(today), end: endOfMonth(today) },
      year:  { start: startOfYear(today),  end: endOfYear(today) },
    };
    const range = ranges[period];
    const colCount = 8;
    const blank = Array(colCount - 1).fill('""').join(',');
    const DIARY_MOODS = ['😠', '😔', '😐', '🙂', '😄'];
    const csvCell = (val: unknown): string => `"${String(val ?? '').replace(/"/g, '""')}"`;
    const stripHtml = (html: string): string =>
      html
        .replace(/<br\s*\/?>/gi, ' ')
        .replace(/<\/p>/gi, ' ')
        .replace(/<\/li>/gi, ' ')
        .replace(/<li>/gi, '• ')
        .replace(/<[^>]+>/g, '')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&nbsp;/g, ' ')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\s+/g, ' ')
        .trim();

    const headers = [
      t('records.csv.date'),
      t('records.csv.heartRate'),
      t('records.csv.respRate'),
      t('records.csv.temp'),
      t('records.csv.mucousMembrane'),
      t('records.csv.weight'),
      t('records.csv.fluidIn'),
      t('records.csv.healthDiary'),
    ];

    const buildRow = (dateStr: string) => {
      const dateLogs = logsByDate[dateStr];
      const hrLog   = dateLogs.find((l: any) => l.heart_rate_bpm != null);
      const rrLog   = dateLogs.find((l: any) => l.resp_rate_bpm != null);
      const tLog    = dateLogs.find((l: any) => l.temp_c != null);
      const mmLog   = dateLogs.find((l: any) => l.mucous_membrane != null);
      const wLog    = dateLogs.find((l: any) => l.weight_kg != null);

      const dayEntries = diaryEntries.filter(e => format(new Date(e.createdAt), 'yyyy-MM-dd') === dateStr);
      let diaryCell: string;
      if (dayEntries.length === 0) {
        diaryCell = '--';
      } else if (allDiaryExpanded) {
        diaryCell = dayEntries
          .map(e => {
            const time = format(new Date(e.createdAt), 'HH:mm');
            const mood = e.mood !== null ? `${DIARY_MOODS[e.mood as number]} ` : '';
            return `${time} ${mood}${stripHtml(e.html)}`.trim();
          })
          .join('\n');
      } else {
        const first = dayEntries[0];
        const time = format(new Date(first.createdAt), 'HH:mm');
        const mood = first.mood !== null ? `${DIARY_MOODS[first.mood as number]} ` : '';
        const suffix = dayEntries.length > 1 ? ` +${dayEntries.length - 1}` : '';
        diaryCell = `${time} ${mood}${stripHtml(first.html)}${suffix}`.trim();
      }

      return [
        dateStr,
        hrLog?.heart_rate_bpm ? `${hrLog.heart_rate_bpm} bpm` : '--',
        rrLog?.resp_rate_bpm  ? `${rrLog.resp_rate_bpm} bpm`  : '--',
        tLog?.temp_c ? (isMetric ? `${tLog.temp_c}°C` : `${(tLog.temp_c * 9/5 + 32).toFixed(1)}°F`) : '--',
        mmLog?.mucous_membrane ? mmLog.mucous_membrane : '--',
        wLog ? formatValue(wLog.weight_kg, 'weight').replace(' ', '') : '--',
        dateLogs.reduce((sum: number, l: any) => sum + (l.fluid_in_ml || 0), 0) || '--',
        diaryCell,
      ].map(csvCell).join(',');
    };

    const sectionLabel = (label: string) => `"${label}",${blank}`;

    const dates = Object.keys(logsByDate)
      .sort()
      .reverse()
      .filter(dateStr => isWithinInterval(parseISO(dateStr), range));

    const lines: string[] = [headers.join(',')];
    let periodLabel = '';

    if (period === 'week') {
      periodLabel = `${t('records.weekOf')} ${format(startOfWeek(today), 'MMM d')} – ${format(endOfWeek(today), 'MMM d, yyyy')}`;
      lines.push(sectionLabel(periodLabel));
      dates.forEach(d => lines.push(buildRow(d)));
    } else if (period === 'month') {
      periodLabel = format(today, 'MMMM yyyy');
      let currentWeekKey = '';
      dates.forEach(dateStr => {
        const weekKey = format(startOfWeek(parseISO(dateStr)), 'yyyy-MM-dd');
        if (weekKey !== currentWeekKey) {
          currentWeekKey = weekKey;
          const ws = startOfWeek(parseISO(dateStr));
          const we = endOfWeek(parseISO(dateStr));
          lines.push(sectionLabel(`${t('records.weekOf')} ${format(ws, 'MMM d')} – ${format(we, 'MMM d, yyyy')}`));
        }
        lines.push(buildRow(dateStr));
      });
    } else {
      periodLabel = format(today, 'yyyy');
      let currentMonth = '';
      dates.forEach(dateStr => {
        const monthKey = format(parseISO(dateStr), 'yyyy-MM');
        if (monthKey !== currentMonth) {
          currentMonth = monthKey;
          lines.push(sectionLabel(format(parseISO(dateStr), 'MMMM yyyy')));
        }
        lines.push(buildRow(dateStr));
      });
    }

    const csv = lines.join('\n');
    const fileName = `KodaCare-${petLabel}-${period}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    return { csv, fileName, periodLabel };
  };

  const downloadCSV = (period: 'week' | 'month' | 'year') => {
    const { csv, fileName } = buildCSV(period);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  };

  const emailCSV = async (period: 'week' | 'month' | 'year') => {
    const { csv, fileName, periodLabel } = buildCSV(period);
    const petLabel = activePet?.name || user?.full_name || 'Pet';
    const ownerLabel = user?.full_name || 'KodaCare';
    const subject = `KodaCare records – ${petLabel} – ${periodLabel}`;
    const body = `Hi,\n\nAttached are the KodaCare records for ${petLabel} (${periodLabel}).\n\n— ${ownerLabel}`;

    // Try OS share sheet first — this surfaces Mail/Gmail with the CSV pre-attached on iOS, Android, macOS.
    try {
      const file = new File([csv], fileName, { type: 'text/csv' });
      const canShareFiles = typeof navigator.canShare === 'function'
        && navigator.canShare({ files: [file] });
      if (navigator.share && canShareFiles) {
        await navigator.share({ files: [file], title: subject, text: body });
        return;
      }
    } catch (err) {
      console.warn('[Records.emailCSV] share failed, falling back to mailto:', err);
    }

    // Fallback: download the CSV, then open the user's mail client with subject + body. mailto: cannot carry attachments.
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);

    const fallbackBody = `${body}\n\n(The CSV file "${fileName}" was downloaded — please attach it manually.)`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(fallbackBody)}`;
  };

  return (
    <div className="w-full">
      {/* Tab bar — top of page on all screen sizes */}
      <div className="sticky top-0 z-20 bg-white pt-2 pb-2 mb-4 flex justify-center">
        <TabSelector
          tabs={[
            { value: 'dashboard', label: t('records.tab.todayEntry') },
            { value: 'archive',   label: t('records.tab.allCards') },
            { value: 'trends',    label: t('trends.title') },
            { value: 'database',  label: t('records.tab.worksheet') },
          ]}
          value={tab}
          onChange={(v) => setTab(v)}
        />
      </div>
      <div className="w-full">
        {/* Today's Entry Tab */}
        {tab === 'dashboard' && (
          <div className="pb-[200px] sm:pb-0 w-full bg-white space-y-4">
            {/* Overview card + toolbar — unified interactive block */}
            <Squircle cornerRadius={24} cornerSmoothing={1} className="overflow-hidden" data-tour="today-card">
              <OverviewCard
                title={`${activePet?.name ?? 'Pet'}'s Health Card`}
                dateStr={format(new Date(), 'yyyy-MM-dd')}
                isMetric={isMetric}
                dateFormat={dateFormat}
                showInfoHint
                weight={todayWeight}
                fluidIn={totalFluidIn}
                foodIn={totalFoodIn}
                hrLog={hrLog}
                rrLog={rrLog}
                tempLog={tempLog}
                mucousMembrane={todayMucousMembrane}
                obsLog={observationsLog}
                onUndoObservations={handleUndoObservations}
              />
              <TodayToolbar
                isMetric={isMetric}
                onAddFluid={(amount) => handleAddFluid(amount, 'chip')}
                onUndoFluid={handleUndoFluid}
                totalFluidIn={totalFluidIn}
                fluidChipCounts={fluidChipCounts}
                onAddFood={(amount) => handleAddFood(amount, 'chip')}
                onUndoFood={handleUndoFood}
                totalFoodIn={totalFoodIn}
                foodChipCounts={foodChipCounts}
                onSaveTemp={handleSaveTemp}
                onUndoTemp={handleUndoTemp}
                todayTemp={tempLog?.temp_c ?? null}
                onSaveMucousMembrane={handleSaveMucousMembrane}
                todayMucousMembrane={todayMucousMembrane}
                observationsMood={observationsMood}
                setObservationsMood={setObservationsMood}
                observationsInput={observationsInput}
                setObservationsInput={setObservationsInput}
                onSaveObservations={handleSaveObservations}
                onUndoObservations={handleUndoObservations}
                observationsLog={observationsLog}
                diaryEntries={todayDiaryEntries}
                onDiarySave={handleDiarySave}
                petName={activePet?.name ?? 'your pet'}
              />
            </Squircle>

            {/* Progress header */}
            <div className="pt-4 pb-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-text-secondary uppercase tracking-widest">{t('records.todaysCard')}</span>
                <span className="text-xs font-semibold text-gray-400">{doneCount}/{todaySteps.length}</span>
              </div>
              <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full rounded-full bg-primary-brown/40 transition-all duration-500"
                  style={{ width: `${(doneCount / todaySteps.length) * 100}%` }}
                />
              </div>
            </div>

            {/* All-done celebration banner */}
            {allDone && (
              <div className="mt-3 mb-1 rounded-2xl bg-positive-green/10 border border-positive-green/30 p-4 flex items-center gap-3">
                <PartyPopper className="w-6 h-6 text-positive-green shrink-0" />
                <div>
                  <p className="font-bold text-positive-green text-sm">{t('records.todaysCardComplete')}</p>
                  <p className="text-positive-green/70 text-xs mt-0.5">{t('records.allThreeLogged')}</p>
                </div>
              </div>
            )}

            {/* 3-step timeline */}
            <div className="pt-4">

              {/* ── Step 1: Heart Rate ── */}
              <TodayStep
                num={1}
                title={t('records.heartRate')}
                done={hrDone}
                unlocked={todaySteps[0].unlocked}
                icon={<Heart className="w-4 h-4 text-white" />}
                iconColor="bg-primary-brown"
                hint={t('records.countFor15s')}
                isNext={todaySteps.find(s => s.unlocked && !s.done)?.id === 'heartRate'}
                summary={hrLog?.heart_rate_bpm ? `${hrLog.heart_rate_bpm} bpm` : undefined}
              >
                {hrDone ? (
                  <div className="bg-primary-brown/10 p-6 rounded-2xl text-center relative">
                    <button onClick={handleUndoHeartRate} className="absolute top-3 right-3 p-3 hover:bg-primary-brown/10 rounded-full text-primary-brown transition-colors" title={t('records.undoHeartRate')}><RotateCcw size={16} /></button>
                    <CheckCircle className="w-12 h-12 text-primary-brown mx-auto mb-2" />
                    <h3 className="font-bold text-primary-brown">{t('records.heartRateRecorded')}</h3>
                    <p className="text-primary-brown/80 text-sm">{hrLog?.heart_rate_bpm} bpm</p>
                  </div>
                ) : (
                  <div className="rounded-2xl bg-white p-6 flex flex-col items-center gap-4 w-full">
                    <div className="flex items-center justify-end w-full">
                      {hrPreCount !== null ? (
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-sm font-black tabular-nums transition-colors ${hrPreCount === 4 ? 'bg-positive-green' : 'bg-primary-brown'}`}>
                          <span className="text-base leading-none">{hrPreCount === 4 ? 'GO!' : hrPreCount}</span>
                        </div>
                      ) : hrTimerSec === null ? (
                        <button type="button" onClick={startHrTimer} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-brown/10 text-primary-brown text-xs font-bold hover:bg-primary-brown/20 active:scale-95 transition-all">
                          <span>▶</span><span>Start Timer</span>
                        </button>
                      ) : hrTimerSec > 0 ? (
                        <div className="flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-primary-brown text-white">
                          <span className="text-sm font-black tabular-nums leading-none">{hrTimerSec}s</span>
                          <button type="button" onClick={stopHrTimer} className="text-[10px] font-bold bg-white/20 hover:bg-white/30 rounded-full px-2 py-0.5 leading-none active:scale-95 transition-all">Stop</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-positive-green/10 text-positive-green text-xs font-bold">✓ Done!</div>
                      )}
                    </div>
                    <div className="flex items-center justify-center gap-4">
                      <div className="relative">
                        <input
                          type="number" inputMode="numeric" enterKeyHint="done"
                          value={hrCount} onChange={(e) => setHrCount(e.target.value)}
                          placeholder="0"
                          className="text-4xl font-bold w-36 pl-3 pr-16 py-2 rounded-xl border border-slate-200 text-[#0d2744] outline-none focus:ring-2 focus:ring-primary-brown/40 text-center"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-text-secondary pointer-events-none">{t('records.beats')}</span>
                      </div>
                      {hrCount && <p className="text-sm font-bold text-primary-brown">{Number(hrCount) * 4} bpm</p>}
                    </div>
                    <button onClick={handleSaveHeartRate} disabled={!hrCount} className="w-full bg-primary-brown text-white py-3 rounded-xl font-bold hover:bg-primary-brown/80 transition-colors disabled:opacity-40">{t('records.saveHeartRate')}</button>
                  </div>
                )}
              </TodayStep>

              {/* ── Step 2: Respiratory Rate ── */}
              <TodayStep
                num={2}
                title={t('records.respRate')}
                done={rrDone}
                unlocked={todaySteps[1].unlocked}
                icon={<Wind className="w-4 h-4 text-white" />}
                iconColor="bg-primary-brown"
                hint={t('records.countBreathsFor15s')}
                isNext={todaySteps.find(s => s.unlocked && !s.done)?.id === 'respRate'}
                summary={rrLog?.resp_rate_bpm ? `${rrLog.resp_rate_bpm} bpm` : undefined}
              >
                {rrDone ? (
                  <div className="bg-primary-brown/10 p-6 rounded-2xl text-center relative">
                    <button onClick={handleUndoRespRate} className="absolute top-3 right-3 p-3 hover:bg-primary-brown/10 rounded-full text-primary-brown transition-colors" title={t('records.undoRespRate')}><RotateCcw size={16} /></button>
                    <CheckCircle className="w-12 h-12 text-primary-brown mx-auto mb-2" />
                    <h3 className="font-bold text-primary-brown">{t('records.respRateRecorded')}</h3>
                    <p className="text-primary-brown/80 text-sm">{rrLog?.resp_rate_bpm} bpm</p>
                  </div>
                ) : (
                  <div className="rounded-2xl bg-white p-6 flex flex-col items-center gap-4 w-full">
                    <div className="flex items-center justify-end w-full">
                      {rrPreCount !== null ? (
                        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-sm font-black tabular-nums transition-colors ${rrPreCount === 4 ? 'bg-positive-green' : 'bg-primary-brown'}`}>
                          <span className="text-base leading-none">{rrPreCount === 4 ? 'GO!' : rrPreCount}</span>
                        </div>
                      ) : rrTimerSec === null ? (
                        <button type="button" onClick={startRrTimer} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary-brown/10 text-primary-brown text-xs font-bold hover:bg-primary-brown/20 active:scale-95 transition-all">
                          <span>▶</span><span>Start Timer</span>
                        </button>
                      ) : rrTimerSec > 0 ? (
                        <div className="flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-primary-brown text-white">
                          <span className="text-sm font-black tabular-nums leading-none">{rrTimerSec}s</span>
                          <button type="button" onClick={stopRrTimer} className="text-[10px] font-bold bg-white/20 hover:bg-white/30 rounded-full px-2 py-0.5 leading-none active:scale-95 transition-all">Stop</button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-positive-green/10 text-positive-green text-xs font-bold">✓ Done!</div>
                      )}
                    </div>
                    <div className="flex items-center justify-center gap-4">
                      <div className="relative">
                        <input
                          type="number" inputMode="numeric" enterKeyHint="done"
                          value={rrCount} onChange={(e) => setRrCount(e.target.value)}
                          placeholder="0"
                          className="text-4xl font-bold w-36 pl-3 pr-16 py-2 rounded-xl border border-slate-200 text-[#0d2744] outline-none focus:ring-2 focus:ring-primary-brown/40 text-center"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-text-secondary pointer-events-none">{t('records.breaths')}</span>
                      </div>
                      {rrCount && <p className="text-sm font-bold text-primary-brown">{Number(rrCount) * 4} bpm</p>}
                    </div>
                    <button onClick={handleSaveRespRate} disabled={!rrCount} className="w-full bg-primary-brown text-white py-3 rounded-xl font-bold hover:bg-primary-brown/80 transition-colors disabled:opacity-40">{t('records.saveRespRate')}</button>
                  </div>
                )}
              </TodayStep>

              {/* ── Step 3: Weight ── */}
              <TodayStep
                num={3}
                title={t('records.weight')}
                done={weightDone}
                unlocked={todaySteps[2].unlocked}
                icon={<Scale className="w-4 h-4 text-white" />}
                iconColor="bg-primary-brown"
                isLast
                isNext={todaySteps.find(s => s.unlocked && !s.done)?.id === 'weight'}
                summary={todayWeight != null ? formatValue(todayWeight, 'weight') : undefined}
              >
                {weightDone ? (
                  <div className="bg-primary-brown/10 p-6 rounded-2xl text-center relative">
                    <button onClick={handleUndoWeight} className="absolute top-3 right-3 p-3 hover:bg-primary-brown/10 rounded-full text-primary-brown transition-colors" title={t('records.undoWeight')}><RotateCcw size={16} /></button>
                    <CheckCircle className="w-12 h-12 text-primary-brown mx-auto mb-2" />
                    <h3 className="font-bold text-primary-brown">{t('records.weightRecorded')}</h3>
                    <p className="text-primary-brown/80 text-sm">{formatValue(todayWeight ?? undefined, 'weight')}</p>
                  </div>
                ) : (
                  <div className="rounded-2xl bg-white p-6 flex flex-col items-center gap-4 w-full">
                    <div className="flex items-center justify-center">
                      <div className="relative">
                        <input
                          type="number" inputMode="decimal" enterKeyHint="done"
                          value={weightStepInput} onChange={(e) => setWeightStepInput(e.target.value)}
                          placeholder={isMetric ? '4.5' : '10.0'}
                          className="text-4xl font-bold w-36 pl-3 pr-14 py-2 rounded-xl border border-slate-200 text-[#0d2744] outline-none focus:ring-2 focus:ring-primary-brown/40 text-center"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-text-secondary pointer-events-none">{isMetric ? 'kg' : 'lbs'}</span>
                      </div>
                    </div>
                    <button onClick={handleSaveWeightStep} disabled={!weightStepInput} className="w-full bg-primary-brown text-white py-3 rounded-xl font-bold hover:bg-primary-brown/80 transition-colors disabled:opacity-40">{t('records.saveWeight')}</button>
                  </div>
                )}
              </TodayStep>

            </div>{/* /timeline */}

          </div>
        )}

        {/* Trends Tab */}
        {tab === 'trends' && (
          <div className="bg-white">
            <Trends />
          </div>
        )}

        {/* Database Tab */}
        {tab === 'database' && (
          <div className="pb-[200px] sm:pb-0 bg-white">
          <div className="space-y-4">
            {/* Action row: icons (right) */}
            {userLogs.length > 0 && (
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={() => setShowAddForm(true)}
                  title={t('records.newEntry')}
                  className="p-2 rounded-lg transition-colors text-text-secondary hover:bg-gray-100"
                >
                  <Plus size={20} />
                </button>
                <div className="flex gap-2">
                  {/* CSV download with period dropdown */}
                  <Dropdown
                    trigger={<Download size={20} />}
                    title={t('records.downloadCsvFor')}
                    items={[
                      { label: t('records.thisWeek'), onClick: () => downloadCSV('week') },
                      { label: t('records.thisMonth'), onClick: () => downloadCSV('month') },
                      { label: t('records.thisYear'), onClick: () => downloadCSV('year') },
                    ]}
                    triggerTitle={t('records.downloadCsvFor')}
                    triggerClassName="p-2 rounded-lg transition-colors text-text-secondary hover:text-text-secondary hover:bg-gray-100"
                  />
                  {/* Email CSV with period dropdown */}
                  <Dropdown
                    trigger={<Mail size={20} />}
                    title="Email CSV for"
                    items={[
                      { label: t('records.thisWeek'), onClick: () => emailCSV('week') },
                      { label: t('records.thisMonth'), onClick: () => emailCSV('month') },
                      { label: t('records.thisYear'), onClick: () => emailCSV('year') },
                    ]}
                    triggerTitle="Email CSV"
                    triggerClassName="p-2 rounded-lg transition-colors text-text-secondary hover:text-text-secondary hover:bg-gray-100"
                  />
                </div>
              </div>
            )}

            {/* Add New Entry when no logs yet */}
            {userLogs.length === 0 && (
              <button
                onClick={() => setShowAddForm(true)}
                className="w-full py-3 rounded-lg font-bold flex items-center justify-center gap-2 transition-colors bg-primary-brown text-white hover:bg-hover-dark"
              >
                <Plus size={20} />
                {t('records.newEntry')}
              </button>
            )}

            {/* Add Entry Modal */}
            <Modal
              open={showAddForm}
              title={t('records.addNewEntry')}
              onClose={() => { setShowAddForm(false); setNewLog({}); }}
              footer={
                <>
                  <button
                    onClick={() => { setShowAddForm(false); setNewLog({}); }}
                    className="flex-1 p-3 rounded-xl font-medium transition-colors text-text-secondary hover:bg-gray-100"
                  >
                    {t('records.cancel')}
                  </button>
                  <button
                    onClick={handleAddLog}
                    disabled={logs.some(l => l.timestamp.startsWith(newLog.date || format(new Date(), 'yyyy-MM-dd')))}
                    className="flex-1 p-3 bg-primary-brown text-white rounded-xl font-bold hover:bg-hover-dark transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('records.saveEntry')}
                  </button>
                </>
              }
            >
              <div className="space-y-5">

                {/* Date */}
                <div>
                  <ThemedDateField
                    label={t('records.date')}
                    value={newLog.date || format(new Date(), 'yyyy-MM-dd')}
                    onChange={(val) => setNewLog({...newLog, date: val})}
                    maxDate={format(new Date(), 'yyyy-MM-dd')}
                  />
                  {logs.some(l => l.timestamp.startsWith(newLog.date || format(new Date(), 'yyyy-MM-dd'))) && (
                    <p className="text-sm text-error-red mt-1">{t('records.dateAlreadyHasEntry')}</p>
                  )}
                </div>

                {/* Vitals */}
                <div className="space-y-2">
                  <div className="text-xs font-bold uppercase tracking-widest pb-1 border-b text-primary-brown border-gray-200">{t('records.vitals')}</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium block mb-1 text-text-secondary">{t('records.heartRate')} (15s count)</label>
                      <input type="number" placeholder="0" value={newLog.heart_rate_count || ''} onChange={(e) => setNewLog({...newLog, heart_rate_count: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40" />
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1 text-text-secondary">{t('records.respRate')} (15s count)</label>
                      <input type="number" placeholder="0" value={newLog.resp_rate_count || ''} onChange={(e) => setNewLog({...newLog, resp_rate_count: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40" />
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1 text-text-secondary">{t('records.temp')} ({isMetric ? '°C' : '°F'})</label>
                      <input type="number" step="0.1" placeholder={isMetric ? '38.5' : '101.3'} value={newLog.temp_c || ''} onChange={(e) => setNewLog({...newLog, temp_c: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40" />
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1 text-text-secondary">{t('records.mucousMembranes')}</label>
                      <select value={newLog.mucous_membrane || ''} onChange={(e) => setNewLog({...newLog, mucous_membrane: e.target.value || undefined})} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40 bg-white">
                        <option value="">--</option>
                        <option value="pink">Pink</option>
                        <option value="red">Red</option>
                        <option value="blue">Blue</option>
                        <option value="white">White</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Daily */}
                <div className="space-y-2">
                  <div className="text-xs font-bold uppercase tracking-widest pb-1 border-b text-primary-brown border-gray-200">{t('records.daily')}</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium block mb-1 text-text-secondary">{t('records.weight')} ({isMetric ? 'kg' : 'lbs'})</label>
                      <input type="number" step="0.1" placeholder="0.0" value={newLog.weight_kg || ''} onChange={(e) => setNewLog({...newLog, weight_kg: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40" />
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1 text-text-secondary">{t('records.fluidIn')} ({isMetric ? 'ml' : 'fl oz'})</label>
                      <input type="number" placeholder="0" value={newLog.fluid_in_ml || ''} onChange={(e) => setNewLog({...newLog, fluid_in_ml: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40" />
                    </div>
                  </div>
                </div>

              </div>
            </Modal>

            {/* Logs Table - One day per row */}
            <div className="relative">
              <div className="rounded-lg border overflow-x-auto border-gray-200">
              {userLogs.length === 0 ? (
                <div className="p-8 text-center bg-gray-50">
                  <p className="text-text-secondary">{t('records.noLogEntriesYet')}</p>
                </div>
              ) : (
                <table className="w-full min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-100 border-gray-200">
                        <th className="px-4 py-3 text-left font-bold sticky left-0 z-10 whitespace-nowrap text-text-secondary bg-gray-100">
                          <button onClick={() => toggleWorksheetSort('date')} className="flex items-center gap-1.5 group">
                            {t('records.date')}
                            {worksheetSortCol === 'date'
                              ? worksheetSortDir === 'desc' ? <ArrowDown size={14} className="text-primary-brown" /> : <ArrowUp size={14} className="text-primary-brown" />
                              : <ArrowDown size={14} className="text-gray-400 group-hover:text-text-secondary" />}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left font-bold whitespace-nowrap text-text-secondary">
                          <button onClick={() => toggleWorksheetSort('hr')} className="flex items-center gap-1.5 group">
                            {t('records.heartRate')} (bpm)
                            {worksheetSortCol === 'hr'
                              ? worksheetSortDir === 'desc' ? <ArrowDown size={14} className="text-primary-brown" /> : <ArrowUp size={14} className="text-primary-brown" />
                              : <ArrowDown size={14} className="text-gray-400 group-hover:text-text-secondary" />}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left font-bold whitespace-nowrap text-text-secondary">
                          <button onClick={() => toggleWorksheetSort('rr')} className="flex items-center gap-1.5 group">
                            {t('records.respRate')} (bpm)
                            {worksheetSortCol === 'rr'
                              ? worksheetSortDir === 'desc' ? <ArrowDown size={14} className="text-primary-brown" /> : <ArrowUp size={14} className="text-primary-brown" />
                              : <ArrowDown size={14} className="text-gray-400 group-hover:text-text-secondary" />}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left font-bold whitespace-nowrap text-text-secondary">
                          <button onClick={() => toggleWorksheetSort('temp')} className="flex items-center gap-1.5 group">
                            {t('records.temp')} ({isMetric ? '°C' : '°F'})
                            {worksheetSortCol === 'temp'
                              ? worksheetSortDir === 'desc' ? <ArrowDown size={14} className="text-primary-brown" /> : <ArrowUp size={14} className="text-primary-brown" />
                              : <ArrowDown size={14} className="text-gray-400 group-hover:text-text-secondary" />}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left font-bold whitespace-nowrap text-text-secondary">
                          <button onClick={() => toggleWorksheetSort('mm')} className="flex items-center gap-1.5 group">
                            {t('records.mucousMembranes')}
                            {worksheetSortCol === 'mm'
                              ? worksheetSortDir === 'desc' ? <ArrowDown size={14} className="text-primary-brown" /> : <ArrowUp size={14} className="text-primary-brown" />
                              : <ArrowDown size={14} className="text-gray-400 group-hover:text-text-secondary" />}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left font-bold whitespace-nowrap text-text-secondary">
                          <button onClick={() => toggleWorksheetSort('weight')} className="flex items-center gap-1.5 group">
                            {t('records.weight')} ({isMetric ? 'kg' : 'lbs'})
                            {worksheetSortCol === 'weight'
                              ? worksheetSortDir === 'desc' ? <ArrowDown size={14} className="text-primary-brown" /> : <ArrowUp size={14} className="text-primary-brown" />
                              : <ArrowDown size={14} className="text-gray-400 group-hover:text-text-secondary" />}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left font-bold whitespace-nowrap text-text-secondary">
                          <button onClick={() => toggleWorksheetSort('fluid')} className="flex items-center gap-1.5 group">
                            {t('records.fluid')} ({isMetric ? 'ml' : 'oz'})
                            {worksheetSortCol === 'fluid'
                              ? worksheetSortDir === 'desc' ? <ArrowDown size={14} className="text-primary-brown" /> : <ArrowUp size={14} className="text-primary-brown" />
                              : <ArrowDown size={14} className="text-gray-400 group-hover:text-text-secondary" />}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left font-bold whitespace-nowrap text-text-secondary">
                          <button onClick={() => toggleWorksheetSort('food')} className="flex items-center gap-1.5 group">
                            {t('records.foodIntake')} ({isMetric ? 'g' : 'oz'})
                            {worksheetSortCol === 'food'
                              ? worksheetSortDir === 'desc' ? <ArrowDown size={14} className="text-primary-brown" /> : <ArrowUp size={14} className="text-primary-brown" />
                              : <ArrowDown size={14} className="text-gray-400 group-hover:text-text-secondary" />}
                          </button>
                        </th>
                        <th className="px-4 py-3 text-left font-bold whitespace-nowrap text-text-secondary">
                          <button onClick={() => setAllDiaryExpanded(v => !v)} className="flex items-center gap-1.5 group">
                            {t('medcard.diary.title')}
                            <ChevronsUpDown size={14} className={allDiaryExpanded ? 'text-primary-brown' : 'text-gray-400 group-hover:text-text-secondary'} />
                          </button>
                        </th>
                        <th className="px-4 py-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {worksheetPagedDates
                        .map((dateStr) => {
                          const dateLogs = logsByDate[dateStr];
                          const hrL  = dateLogs.find(l => l.heart_rate_bpm != null);
                          const rrL  = dateLogs.find(l => l.resp_rate_bpm != null);
                          const tL   = dateLogs.find(l => l.temp_c != null);
                          const mmL  = dateLogs.find(l => l.mucous_membrane != null);
                          const wL   = dateLogs.find(l => l.weight_kg != null);
                          const totalFluidInDay = dateLogs.reduce((sum, l) => sum + (l.fluid_in_ml || 0), 0);
                          const totalFoodInDay = dateLogs.reduce((sum, l) => sum + (l.food_in_mg || 0), 0);
                          const obsLog = dateLogs.find(l => l.observations_text !== undefined || l.mood !== undefined);

                          return (
                            <tr key={dateStr} className="bg-white hover:bg-gray-50 border-t border-gray-200">
                              <td className="px-4 py-3 sticky left-0 z-10 text-text-secondary bg-white">
                                <div className="font-bold">{formatDateByPreference(dateStr, dateFormat)}</div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-text-secondary">
                                {hrL?.heart_rate_bpm ? `${hrL.heart_rate_bpm}` : '--'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-text-secondary">
                                {rrL?.resp_rate_bpm ? `${rrL.resp_rate_bpm}` : '--'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-text-secondary">
                                {tL?.temp_c ? (isMetric ? `${tL.temp_c.toFixed(1)}` : `${(tL.temp_c * 9/5 + 32).toFixed(1)}`) : '--'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-text-secondary capitalize">
                                {mmL?.mucous_membrane ?? '--'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-text-secondary">
                                {wL ? (isMetric ? wL.weight_kg!.toFixed(1) : (wL.weight_kg! * 2.20462).toFixed(1)) : '--'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-text-secondary">
                                {totalFluidInDay ? (isMetric ? totalFluidInDay : Math.round(totalFluidInDay * 0.033814)) : '--'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-text-secondary">
                                {totalFoodInDay ? (isMetric ? (totalFoodInDay / 1000).toFixed(1) : (totalFoodInDay / 28349.5).toFixed(2)) : '--'}
                              </td>
                              <td className="px-4 py-3 text-text-secondary max-w-[200px]">
                                {(() => {
                                  const dayEntries = diaryEntries.filter(e => format(new Date(e.createdAt), 'yyyy-MM-dd') === dateStr);
                                  if (dayEntries.length === 0) return <span className="text-text-secondary">--</span>;
                                  return allDiaryExpanded ? (
                                    <div className="space-y-2">
                                      {dayEntries.map(entry => (
                                        <div key={entry.id} className="text-xs">
                                          <div className="flex items-center gap-1 text-[10px] text-text-secondary/60 mb-0.5">
                                            <span>{format(new Date(entry.createdAt), 'HH:mm')}</span>
                                            {entry.mood !== null && <span>{([<Angry size={11}/>,<Frown size={11}/>,<Meh size={11}/>,<Smile size={11}/>,<Laugh size={11}/>])[entry.mood as number]}</span>}
                                          </div>
                                          <div className="text-text-secondary [&_p]:leading-snug [&_ul]:pl-3 [&_ul]:list-disc [&_strong]:font-semibold [&_em]:italic" dangerouslySetInnerHTML={{ __html: entry.html }} />
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1.5">
                                      {dayEntries[0].mood !== null && <span className="flex-shrink-0">{([<Angry size={13}/>,<Frown size={13}/>,<Meh size={13}/>,<Smile size={13}/>,<Laugh size={13}/>])[dayEntries[0].mood as number]}</span>}
                                      <div className="text-xs text-text-secondary truncate max-w-[140px] [&>*:first-child]:inline" dangerouslySetInnerHTML={{ __html: dayEntries[0].html }} />
                                      {dayEntries.length > 1 && <span className="flex-shrink-0 text-[10px] text-text-secondary/60">+{dayEntries.length - 1}</span>}
                                    </div>
                                  );
                                })()}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-text-secondary">
                                <Dropdown
                                  trigger={<MoreVertical size={18} />}
                                  triggerClassName="p-1 rounded hover:bg-gray-100 transition-colors"
                                  items={[
                                    { label: t('meds.edit'), onClick: () => handleWorksheetEdit(dateStr) },
                                    { label: t('meds.delete'), onClick: () => handleWorksheetDelete(dateStr) },
                                  ]}
                                />
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
              )}
              </div>
              {/* Right-side fade overlay */}
              <div className="pointer-events-none absolute top-0 right-0 bottom-0 w-16 bg-gradient-to-l from-white to-white/0 rounded-r-lg" />
            </div>

            {/* Edit Entry Modal */}
            <Modal
              open={!!worksheetEditDate}
              title={t('records.editEntry')}
              onClose={() => { setWorksheetEditDate(null); setWorksheetEditValues({}); }}
              footer={
                <>
                  <button
                    onClick={() => { setWorksheetEditDate(null); setWorksheetEditValues({}); }}
                    className="flex-1 p-3 rounded-xl font-medium transition-colors text-text-secondary hover:bg-gray-100"
                  >
                    {t('records.cancel')}
                  </button>
                  <button
                    onClick={handleWorksheetEditSave}
                    className="flex-1 p-3 bg-primary-brown text-white rounded-xl font-bold hover:bg-hover-dark transition-colors shadow-sm"
                  >
                    {t('records.saveEntry')}
                  </button>
                </>
              }
            >
              <div className="space-y-5">
                {/* Date display */}
                <div>
                  <label className="text-sm font-medium block mb-1 text-text-secondary">{t('records.date')}</label>
                  <div className="w-full p-2 rounded-xl border text-sm bg-gray-50 border-gray-300 text-text-secondary">
                    {worksheetEditDate ? formatDateByPreference(worksheetEditDate, dateFormat) : ''}
                  </div>
                </div>

                {/* Vitals */}
                <div className="space-y-2">
                  <div className="text-xs font-bold uppercase tracking-widest pb-1 border-b text-primary-brown border-gray-200">{t('records.vitals')}</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium block mb-1 text-text-secondary">{t('records.heartRate')} (15s count)</label>
                      <input type="number" placeholder="0" value={worksheetEditValues.heart_rate_count || ''} onChange={(e) => setWorksheetEditValues({...worksheetEditValues, heart_rate_count: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40" />
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1 text-text-secondary">{t('records.respRate')} (15s count)</label>
                      <input type="number" placeholder="0" value={worksheetEditValues.resp_rate_count || ''} onChange={(e) => setWorksheetEditValues({...worksheetEditValues, resp_rate_count: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40" />
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1 text-text-secondary">{t('records.temp')} ({isMetric ? '°C' : '°F'})</label>
                      <input type="number" step="0.1" placeholder={isMetric ? '38.5' : '101.3'} value={worksheetEditValues.temp_c || ''} onChange={(e) => setWorksheetEditValues({...worksheetEditValues, temp_c: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40" />
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1 text-text-secondary">{t('records.mucousMembranes')}</label>
                      <select value={worksheetEditValues.mucous_membrane || ''} onChange={(e) => setWorksheetEditValues({...worksheetEditValues, mucous_membrane: e.target.value || undefined})} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40 bg-white">
                        <option value="">--</option>
                        <option value="pink">Pink</option>
                        <option value="red">Red</option>
                        <option value="blue">Blue</option>
                        <option value="white">White</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Daily */}
                <div className="space-y-2">
                  <div className="text-xs font-bold uppercase tracking-widest pb-1 border-b text-primary-brown border-gray-200">{t('records.daily')}</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-medium block mb-1 text-text-secondary">{t('records.weight')} ({isMetric ? 'kg' : 'lbs'})</label>
                      <input type="number" step="0.1" placeholder="0.0" value={worksheetEditValues.weight_kg || ''} onChange={(e) => setWorksheetEditValues({...worksheetEditValues, weight_kg: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40" />
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1 text-text-secondary">{t('records.fluidIn')} ({isMetric ? 'ml' : 'fl oz'})</label>
                      <input type="number" placeholder="0" value={worksheetEditValues.fluid_in_ml || ''} onChange={(e) => setWorksheetEditValues({...worksheetEditValues, fluid_in_ml: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40" />
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1 text-text-secondary">{t('records.foodIntake')} ({isMetric ? 'g' : 'oz'})</label>
                      <input type="number" step="0.1" placeholder="0" value={worksheetEditValues.food_in_g || ''} onChange={(e) => setWorksheetEditValues({...worksheetEditValues, food_in_g: e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40" />
                    </div>
                  </div>
                </div>

                {/* Observations */}
                <div className="space-y-2">
                  <div className="text-xs font-bold uppercase tracking-widest pb-1 border-b text-text-secondary border-gray-200">{t('records.observations')}</div>
                  <div>
                    <label className="text-xs font-medium block mb-2 text-text-secondary">{t('records.howAreYouFeeling')}</label>
                    <div className="flex justify-between gap-2">
                      {([<Angry size={20}/>,<Frown size={20}/>,<Meh size={20}/>,<Smile size={20}/>,<Laugh size={20}/>] as const).map((icon, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setWorksheetEditValues({...worksheetEditValues, mood: worksheetEditValues.mood === i ? null : i})}
                          className={cn(
                            'flex-1 py-1.5 rounded-xl border-2 transition-all flex items-center justify-center text-background-light',
                            worksheetEditValues.mood === i ? 'border-gray-500 bg-gray-100' : 'border-gray-200 hover:bg-gray-50'
                          )}
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium block mb-1 text-text-secondary">{t('records.observations')}</label>
                    <textarea
                      rows={5}
                      placeholder={t('records.observationsPlaceholder')}
                      value={worksheetEditValues.observations_text || ''}
                      onChange={(e) => setWorksheetEditValues({...worksheetEditValues, observations_text: e.target.value})}
                      className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-[#0d2744] font-semibold outline-none focus:ring-2 focus:ring-highlight-dark/40 resize-none"
                    />
                  </div>
                </div>
              </div>
            </Modal>

            {/* Filter + Pagination bar — always rendered for consistent nav-bar clearance */}
            <div className="flex items-center justify-between pb-9 text-text-secondary">
              {/* Filter on the left */}
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-text-secondary">{t('records.filterBy')}</label>
                <Dropdown
                  align="left"
                  triggerClassName="flex items-center"
                  trigger={
                    <span className="flex items-center gap-1.5 px-3 py-2 border rounded-xl border-gray-300 bg-white text-sm text-text-primary">
                      {sortByPeriod === 'week' ? t('records.week') : sortByPeriod === 'month' ? t('records.month') : t('records.year')}
                      <ChevronDown size={14} className="text-text-secondary" />
                    </span>
                  }
                  items={[
                    { label: t('records.week'),  onClick: () => { setSortByPeriod('week');  setWorksheetPage(1); } },
                    { label: t('records.month'), onClick: () => { setSortByPeriod('month'); setWorksheetPage(1); } },
                    { label: t('records.year'),  onClick: () => { setSortByPeriod('year');  setWorksheetPage(1); } },
                  ]}
                />
              </div>
              {/* Pagination on the right — always shown when there's data */}
              {filteredDates.length > 0 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setWorksheetPage(p => Math.max(1, p - 1))}
                    disabled={worksheetPage === 1}
                    className="p-2 rounded-lg transition-colors disabled:opacity-30 hover:bg-gray-100"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <span className="text-sm font-medium px-2">{worksheetPage} / {worksheetTotalPages}</span>
                  <button
                    onClick={() => setWorksheetPage(p => Math.min(worksheetTotalPages, p + 1))}
                    disabled={worksheetPage === worksheetTotalPages}
                    className="p-2 rounded-lg transition-colors disabled:opacity-30 hover:bg-gray-100"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Cards Archive Tab */}
      {tab === 'archive' && (
        <div className="space-y-6 pb-[200px] sm:pb-0 w-full bg-white">
          {userLogs.length === 0 ? (
            <div className="p-8 text-center rounded-lg mt-4 bg-gray-50">
              <Archive className="mx-auto mb-4 text-gray-400" size={48} />
              <p className="text-text-secondary">{t('records.noLogEntriesYet')}</p>
            </div>
          ) : (
            <ArchiveCalendar logs={userLogs} isMetric={isMetric} dateFormat={dateFormat} />
          )}
        </div>
      )}

      {/* Delete Confirmation Modal - rendered outside tabs so it works everywhere */}
      {deleteModal}
      <ToastMount />
      </div>
    </div>
  );
}

function ArchiveCalendar({ logs, isMetric, dateFormat }: any) {
  const { t, language } = useLanguage();
  const { activePet: archivePet } = usePet();
  const { showToast, ToastMount: ArchiveToastMount } = useToast();
  const [sharing, setSharing] = useState(false);
  const logsByDate: Record<string, any[]> = useMemo(() => {
    return logs.reduce((acc: Record<string, any[]>, log: any) => {
      const dateStr = log.timestamp.split('T')[0];
      if (!acc[dateStr]) acc[dateStr] = [];
      acc[dateStr].push(log);
      return acc;
    }, {});
  }, [logs]);

  const uniqueDates = useMemo(() => Object.keys(logsByDate).sort().reverse(), [logsByDate]);
  const latestDate = uniqueDates[0] ?? null;
  const [currentMonth, setCurrentMonth] = useState(() => latestDate ? startOfMonth(parseISO(latestDate)) : startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState<string | null>(latestDate);

  const monthStart = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 });
  const monthEnd = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 });
  const days: Date[] = [];
  let cursor = monthStart;
  while (cursor <= monthEnd) {
    days.push(cursor);
    cursor = addDays(cursor, 1);
  }

  const selectedDayLogs = selectedDate ? (logsByDate[selectedDate] ?? []) : [];
  const selectedWeightLog     = selectedDayLogs.find((l: any) => l.weight_kg != null);
  const selectedHrLog          = selectedDayLogs.find((l: any) => l.heart_rate_bpm != null);
  const selectedRrLog          = selectedDayLogs.find((l: any) => l.resp_rate_bpm != null);
  const selectedTempLog        = selectedDayLogs.find((l: any) => l.temp_c != null);
  const selectedMucousLog      = selectedDayLogs.find((l: any) => l.mucous_membrane != null);
  const selectedTotalFluidIn   = selectedDayLogs.reduce((sum: number, l: any) => sum + (l.fluid_in_ml || 0), 0);
  const selectedTotalFoodIn    = selectedDayLogs.reduce((sum: number, l: any) => sum + (l.food_in_mg || 0), 0);
  const selectedObsLog = selectedDayLogs.find((l: any) => l.observations_text !== undefined || l.mood !== undefined);
  const weekdayLabels = Array.from({ length: 7 }, (_, i) =>
    format(addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), i), 'EEE', { locale: getDateLocale(language) })
  );
  const cardRef = useRef<HTMLDivElement>(null);

  const captureCanvas = async (): Promise<Blob | null> => {
    if (!cardRef.current) return null;
    const canvas = await html2canvas(cardRef.current, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: null,
      ignoreElements: (el) => el instanceof HTMLElement && el.dataset.noCapture !== undefined,
    });
    return new Promise((resolve, reject) =>
      canvas.toBlob(b => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/png')
    );
  };

  const handleShare = async () => {
    console.log('[Records.handleShare] start', { selectedDate, sharing, hasCardRef: !!cardRef.current });
    if (!selectedDate || sharing) return;
    setSharing(true);
    try {
      let blob: Blob | null = null;
      try {
        blob = await captureCanvas();
        console.log('[Records.handleShare] capture result', { hasBlob: !!blob, size: blob?.size });
      } catch (err) {
        console.warn('[Records.handleShare] captureCanvas failed:', err);
        showToast('Could not create image', 'error');
      }

      if (!blob) {
        console.warn('[Records.handleShare] no blob produced');
        showToast('Could not create image', 'error');
        return;
      }

      const fileName = `KodaCare-${selectedDate}.png`;
      const file = new File([blob], fileName, { type: 'image/png' });
      const canShareFiles = typeof navigator.canShare === 'function'
        && navigator.canShare({ files: [file] });
      console.log('[Records.handleShare] share support', { hasShare: !!navigator.share, canShareFiles });

      if (navigator.share && canShareFiles) {
        try {
          await navigator.share({ title: 'KodaCare Daily Summary', files: [file] });
          console.log('[Records.handleShare] navigator.share completed');
          return;
        } catch (err) {
          console.warn('[Records.handleShare] navigator.share failed, falling back to download:', err);
          // fall through to download fallback (covers AbortError on desktops with no share target)
        }
      }

      const url = URL.createObjectURL(blob);
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.rel = 'noopener';
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        console.log('[Records.handleShare] download click dispatched');
      } catch (err) {
        console.warn('[Records.handleShare] download failed, opening in new tab:', err);
        window.open(url, '_blank', 'noopener');
        showToast('Image opened in new tab', 'info');
      } finally {
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
    } catch (err) {
      console.warn('[Records.handleShare] outer error:', err);
      showToast('Could not share image', 'error');
    } finally {
      setSharing(false);
    }
  };

  return (
    <>
    <div className="space-y-6">
      {selectedDate && (
        <Squircle asChild cornerRadius={24} cornerSmoothing={1}>
        <div ref={cardRef} className="overflow-hidden">
          <OverviewCard
            title={`${archivePet?.name ?? 'Pet'}'s Health Card`}
            dateStr={selectedDate}
            isMetric={isMetric}
            dateFormat={dateFormat}
            showInfoHint={false}
            weight={selectedWeightLog?.weight_kg ?? null}
            fluidIn={selectedTotalFluidIn}
            foodIn={selectedTotalFoodIn}
            hrLog={selectedHrLog}
            rrLog={selectedRrLog}
            tempLog={selectedTempLog}
            mucousMembrane={selectedMucousLog?.mucous_membrane ?? null}
            obsLog={selectedObsLog}
            onShare={handleShare}
            showBranding
              sharing={sharing}
          />
        </div>
        </Squircle>
      )}

      <div className="w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-text-primary">
            {format(currentMonth, 'MMMM yyyy')}
          </h3>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentMonth(m => subMonths(m, 1))}
              className="p-2 rounded-full transition-colors hover:bg-gray-100 text-text-secondary"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => {
                const today = format(new Date(), 'yyyy-MM-dd');
                setCurrentMonth(startOfMonth(new Date()));
                setSelectedDate(today);
              }}
              className="px-3 py-1 rounded-lg text-xs font-bold transition-colors text-text-secondary hover:bg-gray-100"
            >
              {t('records.today')}
            </button>
            <button
              onClick={() => setCurrentMonth(m => addMonths(m, 1))}
              className="p-2 rounded-full transition-colors hover:bg-gray-100 text-text-secondary"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 mb-1">
          {weekdayLabels.map(wd => (
            <div key={wd} className="text-center text-[11px] font-bold uppercase tracking-wide py-1 text-gray-400">
              {wd}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1 p-3 rounded-2xl border bg-gray-50 border-gray-100">
          {days.map(date => {
            const key = format(date, 'yyyy-MM-dd');
            const dayLogs = logsByDate[key] ?? [];
            const hasEntries = dayLogs.length > 0;
            const dayWeightLog = dayLogs.find((l: any) => l.weight_kg != null);
            const dayHrLog = dayLogs.find((l: any) => l.heart_rate_bpm != null);
            const dayRrLog = dayLogs.find((l: any) => l.resp_rate_bpm != null);
            const dayTempLog = dayLogs.find((l: any) => l.temp_c != null);
            const dayMucousLog = dayLogs.find((l: any) => l.mucous_membrane != null);
            const dayFluid = dayLogs.reduce((sum: number, l: any) => sum + (l.fluid_in_ml || 0), 0);
            const dayObsLog = dayLogs.find((l: any) => l.observations_text !== undefined || l.mood !== undefined);
            const isSelected = !!(selectedDate && isSameDay(date, parseISO(selectedDate)) && isSameMonth(date, currentMonth));

            return (
              <button
                key={key}
                onClick={() => setSelectedDate(key)}
                disabled={!isSameMonth(date, currentMonth)}
                className={cn(
                  'relative flex flex-col items-center justify-start pt-1.5 pb-1 rounded-xl transition-all duration-150 select-none h-14 sm:h-18',
                  isSameMonth(date, currentMonth) ? 'cursor-pointer' : 'opacity-30 cursor-default',
                  selectedDate && isSameDay(date, parseISO(selectedDate)) && isSameMonth(date, currentMonth)
                    ? 'bg-primary-brown text-white shadow-md scale-[1.03]'
                    : isToday(date) && isSameMonth(date, currentMonth)
                    ? 'bg-primary-brown/10 text-primary-brown'
                    : 'hover:bg-white text-text-secondary'
                )}
              >
                <span className={cn('text-sm font-bold leading-none', isSelected && 'text-white')}>
                  {format(date, 'd')}
                </span>
                <div className="flex gap-0.5 mt-1.5 flex-wrap justify-center">
                  {(dayHrLog || dayRrLog) && <span className={cn('w-1.5 h-1.5 rounded-full', isSelected ? 'bg-white/80' : 'bg-primary-brown')} />}
                  {dayMucousLog && <span className={cn('w-1.5 h-1.5 rounded-full', isSelected ? 'bg-white/80' : 'bg-pink-400')} />}
                  {dayTempLog && <span className={cn('w-1.5 h-1.5 rounded-full', isSelected ? 'bg-white/80' : 'bg-amber-400')} />}
                  {dayObsLog && <span className={cn('w-1.5 h-1.5 rounded-full', isSelected ? 'bg-white/80' : 'bg-gray-400')} />}
                  {dayFluid > 0 && <span className={cn('w-1.5 h-1.5 rounded-full', isSelected ? 'bg-white/80' : 'bg-[#28b5b3]')} />}
                </div>
                <span className={cn('text-[10px] mt-1 font-medium', isSelected ? 'text-white/80' : 'text-gray-400')}>
                  {hasEntries ? `${dayLogs.length} ${dayLogs.length === 1 ? t('records.entry') : t('records.entries')}` : '—'}
                </span>
                {/* Today is indicated by the primary-brown background fill above; no outer ring. */}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 px-1">
          <span className="flex items-center gap-1.5 text-xs"><span className="w-2 h-2 rounded-full bg-primary-brown" /><span className="text-text-secondary">{t('records.vitals')}</span></span>
          <span className="flex items-center gap-1.5 text-xs"><span className="w-2 h-2 rounded-full bg-pink-400" /><span className="text-text-secondary">{t('records.mucousMembranes')}</span></span>
          <span className="flex items-center gap-1.5 text-xs"><span className="w-2 h-2 rounded-full bg-amber-400" /><span className="text-text-secondary">{t('records.temperature')}</span></span>
          <span className="flex items-center gap-1.5 text-xs"><span className="w-2 h-2 rounded-full bg-gray-400" /><span className="text-text-secondary">{t('records.observations')}</span></span>
          <span className="flex items-center gap-1.5 text-xs"><span className="w-2 h-2 rounded-full bg-[#28b5b3]" /><span className="text-text-secondary">{t('records.fluids')}</span></span>
        </div>
      </div>

    </div>
    <ArchiveToastMount />
    </>
  );
}

function DashCard({ title, icon, color, children }: { title: string; icon: React.ReactNode; color: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl overflow-hidden border-2 border-transparent bg-white">
      <div className={cn("p-4 flex justify-between items-center", color)}>
        <div className="flex items-center gap-3">
          {icon}
          <h3 className="text-xl font-bold text-white">{title}</h3>
        </div>
      </div>
      <div className="p-6 flex flex-col items-center">
        {children}
      </div>
    </div>
  );
}


